/**
 * CallContext — o ciclo da ligação do chat no front (Task 11, 24/10/2026).
 *
 * O Augusto abriu o pedido por dois motivos: a ligação de voz tomava a tela inteira e, do outro lado, não
 * aparecia NADA (nem aviso, nem som). Este provider faz o outro lado tocar e transforma a voz numa janelinha:
 *
 * - **Chegando** (`chegando`): o servidor manda `chamada:chegando` no canal pessoal; entra na lista (sem repetir
 *   o id), toca a campainha enquanto houver alguma, e sai em `chamada:parou`, `chamada:encerrada` ou quando o
 *   tempo acaba. O prazo é `min(segundos_restantes, 30)` s CONTADOS DE QUANDO O EVENTO CHEGOU — nunca
 *   `expira_em − Date.now()`: o relógio do computador da pessoa pode estar errado.
 * - **A ligação atual** (`ligacao`): `chamando` → `em_curso` → `terminou`. Quem liga vê a janelinha NO CLIQUE
 *   (antes de o `/initiate` responder) e ouve o "chamando"; `chamada:atendida` a leva a `em_curso`;
 *   `chamada:encerrada` a leva a `terminou` com o desfecho em texto, e a janelinha fecha sozinha 3 s depois.
 * - **Os dois lados saem do mesmo jeito**: na conversa direta `/end` encerra para os dois; no grupo `/leave`
 *   só tira a pessoa (a ligação acaba quando o último sai).
 * - **Quem liga nunca fica preso em "Chamando…" (R1, R2, R3, sobras das ligações)**: o evento ao vivo pode se
 *   perder (o pub/sub do Redis não guarda nada) e o job do servidor pode estar parado, então a ligação se
 *   CONFERE pelo `/estado` (`conferirPeloServidor`): passados 35 s da discagem (`teto`; no grupo grande, que
 *   ninguém toca, não há teto), na volta do sino depois de uma queda e quando outra pessoa aparece na sala de
 *   quem ainda chama. O `chamada:encerrada` que chega antes da resposta do `/initiate` é guardado e aplicado
 *   quando ele responde com o mesmo id. O `useCall` não tem ciclo de reconexão: a reconciliação mora aqui.
 *
 * O desenho (`docs/superpowers/specs/2026-10-22-ligacoes-do-chat-tela.md`, §9) é o contrato dos tipos abaixo.
 * Nada aqui usa `alert`: o desfecho vai na própria janelinha, a falha de Atender em linha no cartão (no celular
 * um toast ficaria ATRÁS do aviso), e o que sobra vai para toast.
 *
 * O barramento `eventosDaChamada` existe porque o `NotificationProvider` e este provider são irmãos na árvore.
 * O `CallModal` deixou de ser montado aqui e saiu do código na Task 13 (ficou sem chamador): o organograma
 * liga por `startCall`, como o chat.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { AvisoDeChamada } from "@/components/calls/AvisoDeChamada";
import { JanelaDaLigacao } from "@/components/calls/JanelaDaLigacao";
import { useCall, type EndReason } from "@/hooks/useCall";
import { aguardandoAlguemEntrar } from "@/lib/chamadas/aguardandoAlguem";
import { registrarAntesDeSair } from "@/lib/chamadas/antesDeSair";
import {
  ouvirEventosDeChamada,
  ouvirVoltaDoSino,
  type ChamadaChegando,
  type EventoDeChamada,
} from "@/lib/chamadas/eventosDaChamada";
import { focoEmCampoEditavel } from "@/lib/chamadas/focoDoAviso";
import {
  marcarEmLigacao,
  tocarBipeDeEspera,
  tocarCampainha,
  tocarChamando,
} from "@/lib/chamadas/toque";
import { getTransportMode } from "@/lib/realtime-store";
import {
  ehMensagemDaChamada,
  ehMensagemDeIr,
  ehPerguntaDaChamada,
  type RespostaDaPerguntaDaChamada,
} from "@/lib/swPush";
import { encerrarLigacaoAoSair, estadoDaLigacao, recusarLigacao } from "@/services/callsService";
import type { Desfecho, EstadoDaLigacao, Midia } from "@/types/calls";

/* ── tipos ───────────────────────────────────────────────────────────────────────────────────────── */

export type TipoDeSala = "DM" | "GROUP" | "SECTOR_AUTO";

export interface StartCallArgs {
  /** A conversa onde ligar (o servidor acha a direta ou usa a do grupo). */
  room_id?: string;
  /** Ligar para uma pessoa sem conversa aberta (organograma, lista de colaboradores). */
  target_user_id?: string;
  /** O que a janelinha mostra: o nome da pessoa (conversa direta) ou do grupo. */
  titulo: string;
  midia: Midia;
  /** O tipo da conversa, quando quem chama já o tem à mão. Com `target_user_id` é sempre `DM`. */
  room_type?: TipoDeSala;
  /** Numa conversa direta aberta, quem está do outro lado (a foto da janelinha). */
  outro_lado_id?: string;
}

interface LigacaoComum {
  /** `null` do clique até a resposta do `/initiate`: a janelinha aparece no clique, não 1–2 s depois. */
  callSessionId: string | null;
  roomId: string | null;
  roomType: TipoDeSala | null;
  /** Conversa direta: quem está do outro lado (a foto). Grupo: `null`. */
  outroLadoId: string | null;
  midia: Midia;
  titulo: string;
  souQuemLigou: boolean;
  /** Quantas pessoas estão tocando (0 = ninguém: grupo grande). */
  tocaPara: number;
  /** O desfecho em texto, só em `terminou`. `null` = o fim normal ("Chamada encerrada"). */
  aviso: string | null;
}

/** Discriminada por `fase`: em `em_curso` o id e o início existem, e `startedAt={iniciadaEm}` compila sem `!`. */
export type LigacaoAtual =
  | (LigacaoComum & { fase: "chamando" | "terminou"; iniciadaEm: string | null })
  | (LigacaoComum & { fase: "em_curso"; callSessionId: string; iniciadaEm: string });

export interface CallContextValue {
  startCall: (args: StartCallArgs) => Promise<void>;
  /**
   * Atende uma ligação (o aviso) ou entra numa em curso (a faixa "Entrar" do grupo). Resolve também no 409
   * ("A ligação já terminou."), que o provider avisa por toast; REJEITA nas outras falhas, sem toast: quem
   * chamou mostra o erro em linha. `titulo` serve a quem não tem o aviso (a faixa) e quer o nome do grupo.
   */
  atender: (id: string, titulo?: string) => Promise<void>;
  /** Sempre resolve: em falha tira o aviso mesmo assim e avisa por toast. */
  recusar: (id: string) => Promise<void>;
  endCall: () => void;
  ligacao: LigacaoAtual | null;
  chegando: ChamadaChegando[];
  /** Há uma ligação ativa (tocando ou em curso). Em `terminou` já não há. */
  isInCall: boolean;
  status: string;
}

/* ── textos e auxiliares ─────────────────────────────────────────────────────────────────────────── */

/** O toque dura 30 s no servidor; o front nunca segura o aviso por mais que isso. */
const TOQUE_MAXIMO_S = 30;
/** Quanto tempo a janelinha de `terminou` fica na tela antes de fechar sozinha. */
const FECHA_DEPOIS_DE_MS = 3_000;
/**
 * Quanto a discagem dura antes de se perguntar ao servidor: os 30 s do toque + 5 s de folga para o job que fecha o
 * toque vencido. Passou disso, quem liga não pode seguir em "Chamando…" com o "tuuu" (R1).
 */
const TETO_DA_DISCAGEM_S = 35;
/** A leitura do teto falhou (rede piscando): espera isto e lê de novo antes de dar a ligação por não atendida. */
const ESPERA_DA_SEGUNDA_LEITURA_MS = 3_000;
const TEXTO_JA_TERMINOU = "A ligação já terminou.";
const TEXTO_NAO_ATENDEU = "Não foi possível atender. Tente de novo.";
const TEXTO_NAO_LIGOU = "Não foi possível ligar. Tente de novo.";

function tipoDaSala(valor: string | null | undefined): TipoDeSala | null {
  return valor === "DM" || valor === "GROUP" || valor === "SECTOR_AUTO" ? valor : null;
}

/** Grupo: quem sai só sai (`/leave`). O resto (conversa direta, tipo desconhecido) encerra para os dois (`/end`). */
function ehGrupo(ligacao: { roomType: TipoDeSala | null }): boolean {
  return ligacao.roomType === "GROUP" || ligacao.roomType === "SECTOR_AUTO";
}

function statusDoErro(erro: unknown): number | undefined {
  return (erro as { response?: { status?: number } } | null)?.response?.status;
}

function detalheDoErro(erro: unknown): unknown {
  return (erro as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail;
}

/**
 * O texto do desfecho de um `/initiate` que falhou (formas reais do servidor, `inicio.py` e `calls.py`): todo
 * `detail` em objeto que traga `mensagem` já a traz pronta — 409 `em_outra_ligacao` ("Juarez está em outra
 * ligação."), 429 `ligacao_repetida` ("Espere 3 s para ligar de novo."); 422 traz o `detail` em texto; o resto
 * (429 do teto de ligações, sem `mensagem`; rede; 5xx) vira o texto genérico.
 */
function textoDoErroAoLigar(erro: unknown): string {
  const status = statusDoErro(erro);
  const detalhe = detalheDoErro(erro);
  if (detalhe && typeof detalhe === "object") {
    const mensagem = (detalhe as { mensagem?: unknown }).mensagem;
    if (typeof mensagem === "string" && mensagem) return mensagem;
  }
  if (status === 422 && typeof detalhe === "string" && detalhe) return detalhe;
  return TEXTO_NAO_LIGOU;
}

/** "Ninguém atendeu" no grupo (o título é o nome do grupo, que não "atende"); na conversa direta, o nome da pessoa. */
function textoDeNinguemAtendeu(ligacao: LigacaoAtual): string {
  return ehGrupo(ligacao) ? "Ninguém atendeu" : `${ligacao.titulo} não atendeu`;
}

/** O desfecho em texto para quem ligou. `cancelada` não passa por aqui: a janelinha fecha sem texto. */
function textoDoDesfecho(ligacao: LigacaoAtual, desfecho: Desfecho): string | null {
  if (!ligacao.souQuemLigou) return null;
  if (desfecho === "nao_atendida") return textoDeNinguemAtendeu(ligacao);
  if (desfecho === "recusada") return `${ligacao.titulo} recusou`;
  return null;
}

/** Quanto o aviso fica na tela: `segundos_restantes`, com o teto de 30 s. */
function prazoDoToqueEmMs(segundosRestantes: number): number {
  const segundos = Number.isFinite(segundosRestantes)
    ? Math.max(0, Math.min(segundosRestantes, TOQUE_MAXIMO_S))
    : TOQUE_MAXIMO_S;
  return segundos * 1000;
}

/** Por que a ligação atual é conferida pelo servidor (`conferirPeloServidor`). Só o `teto` pode dar um fim sozinho. */
type MotivoDaConferencia = "volta-do-sino" | "entrou-gente" | "teto";

const CallContext = createContext<CallContextValue | null>(null);

/* ── o provider ──────────────────────────────────────────────────────────────────────────────────── */

export function CallProvider({ children }: { children: ReactNode }) {
  const call = useCall();
  // O hook devolve um objeto novo a cada render; só `initiate/join/end/leave` são estáveis. Os callbacks
  // abaixo leem o que precisam por aqui para manter identidade estável (o contexto não re-renderiza o app).
  const callRef = useRef(call);
  callRef.current = call;
  const navigate = useNavigate();
  // O `navigate` troca de identidade a cada rota; o ouvinte do service worker lê o de agora por aqui e não se refaz.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const local = useLocation();

  /* ── a ligação atual ─────────────────────────────────────────────────────────────────────────── */

  const [ligacao, setLigacaoNoEstado] = useState<LigacaoAtual | null>(null);
  // O espelho em `ref` é o que os ouvintes e as continuações assíncronas leem: o estado do render deles
  // estaria velho. Toda escrita passa por `definirLigacao`.
  const ligacaoRef = useRef<LigacaoAtual | null>(null);
  // Uma geração por ligação: a janelinha remonta limpa (modo do vídeo, erro, estado da sala) mesmo quando
  // uma ligação substitui a outra sem passar por `null`.
  const [geracao, setGeracao] = useState(0);
  const temporizadorDeFechar = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** "Cancelei antes de o /initiate responder": ainda não há id para encerrar (desenho §9). */
  const cancelarAoResponder = useRef(false);
  /**
   * O `/initiate` da minha discagem está no ar. Enquanto durar, o hook ainda não conhece a sessão (`end` e
   * `leave` não fazem nada sem ela), mesmo que o `chamada:atendida` já tenha dado o id à ligação.
   */
  const iniciando = useRef(false);
  /**
   * O `chamada:encerrada` que chegou ANTES de o `/initiate` responder (a ligação ainda sem id): guardado em vez de
   * descartado, e aplicado pelo `startCall` quando a resposta traz o MESMO id (R3). Zera a cada discagem nova.
   */
  const fimAntecipado = useRef<{ id: string; desfecho: Desfecho } | null>(null);
  /** A espera entre a 1ª e a 2ª leitura do teto: o desmonte do provider a cancela. */
  const esperaDaLeitura = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * O id da ligação que o "Sair do sistema" já encerrou no servidor (com o token de quem sai, R4): o desmonte do
   * provider que vem logo depois não manda um 2º pedido, que sairia sem token (o logout já o apagou) e levaria 401.
   */
  const encerradaAoSair = useRef<string | null>(null);

  const definirLigacao = useCallback(
    (proxima: LigacaoAtual | null | ((anterior: LigacaoAtual | null) => LigacaoAtual | null)) => {
      const nova = typeof proxima === "function" ? proxima(ligacaoRef.current) : proxima;
      ligacaoRef.current = nova;
      setLigacaoNoEstado(nova);
    },
    []
  );

  const limparFechamento = useCallback(() => {
    if (temporizadorDeFechar.current) clearTimeout(temporizadorDeFechar.current);
    temporizadorDeFechar.current = null;
  }, []);

  const abrirLigacao = useCallback(
    (nova: LigacaoAtual) => {
      cancelarAoResponder.current = false;
      setGeracao((g) => g + 1);
      definirLigacao(nova);
    },
    [definirLigacao]
  );

  /** A ligação acabou: fica 3 s na tela com o desfecho (`aviso`) e fecha sozinha. */
  const terminar = useCallback(
    (aviso: string | null) => {
      const atual = ligacaoRef.current;
      if (!atual || atual.fase === "terminou") return;
      definirLigacao({ ...atual, fase: "terminou", aviso });
      limparFechamento();
      temporizadorDeFechar.current = setTimeout(() => {
        temporizadorDeFechar.current = null;
        definirLigacao((a) => (a && a.fase === "terminou" ? null : a));
      }, FECHA_DEPOIS_DE_MS);
    },
    [definirLigacao, limparFechamento]
  );

  /** O X da janelinha em `terminou` (e só nela). */
  const fechar = useCallback(() => {
    limparFechamento();
    definirLigacao((a) => (a && a.fase === "terminou" ? null : a));
  }, [definirLigacao, limparFechamento]);

  /**
   * `/end` na conversa direta (encerra para os dois), `/leave` no grupo (só sai). Sem id, não há o que encerrar.
   * `motivo` só vale no `/end` (o `/leave` não tem): "normal" é quem desligou; o teto da discagem manda "timeout".
   */
  const encerrarNoServidor = useCallback(
    async (atual: LigacaoAtual, motivo: EndReason = "normal") => {
      if (atual.callSessionId === null) return;
      if (ehGrupo(atual)) await callRef.current.leave();
      else await callRef.current.end(motivo);
    },
    []
  );

  /**
   * O fim da ligação com o desfecho que o servidor gravou, venha ele do evento, do `/estado` ou do fim
   * antecipado. `cancelada` de quem ligou (fui eu) fecha na hora, sem passar por `terminou` e sem texto; o resto
   * vai a `terminou` com o texto do desfecho (só quem ligou tem texto).
   */
  const aplicarFim = useCallback(
    (atual: LigacaoAtual, desfecho: Desfecho) => {
      if (desfecho === "cancelada" && atual.souQuemLigou) {
        definirLigacao(null);
      } else {
        terminar(textoDoDesfecho(atual, desfecho));
      }
    },
    [definirLigacao, terminar]
  );

  /**
   * Pergunta ao servidor como está a ligação atual e acerta a tela (R1, R2): o evento ao vivo pode ter se perdido
   * (o sino piscou) e o job que fecha o toque vencido pode estar parado. `encerrada` aplica o fim; `atendida` leva
   * quem ligou a `em_curso` (o "tuuu" para porque a fase sai de `chamando`). Só o `teto` conclui "ninguém
   * atendeu" (e então também encerra a sessão no servidor) — os outros motivos só ajudam a tela a alcançar o
   * servidor, e uma leitura que falha não faz nada. No `teto` a leitura que falha ganha uma 2ª tentativa depois
   * de 3 s, e falhando de novo a ligação termina: nunca fica em "Chamando…". Nunca lança. A resposta só vale para a ligação que foi perguntada: se a pessoa
   * já cancelou ou ligou de novo, a leitura velha é descartada.
   */
  const conferirPeloServidor = useCallback(
    async (motivo: MotivoDaConferencia) => {
      const perguntada = ligacaoRef.current;
      if (!perguntada || perguntada.fase === "terminou" || perguntada.callSessionId === null)
        return;
      const id = perguntada.callSessionId;
      const aindaEhEla = () => {
        const agora = ligacaoRef.current;
        return agora && agora.fase !== "terminou" && agora.callSessionId === id ? agora : null;
      };
      const ler = async (): Promise<EstadoDaLigacao | null> => {
        try {
          return await estadoDaLigacao(id);
        } catch {
          return null;
        }
      };

      let estado = await ler();
      if (estado === null && motivo === "teto") {
        await new Promise<void>((seguir) => {
          esperaDaLeitura.current = setTimeout(() => {
            esperaDaLeitura.current = null;
            seguir();
          }, ESPERA_DA_SEGUNDA_LEITURA_MS);
        });
        if (!aindaEhEla()) return;
        estado = await ler();
      }

      const atual = aindaEhEla();
      if (!atual) return;
      // "Ninguém atendeu" só se conclui de uma ligação que AINDA chama: se o `atendida` chegou enquanto a leitura
      // voava, a leitura é mais velha que a tela e não pode derrubar a conversa que começou.
      const ninguemAtendeu = motivo === "teto" && atual.fase === "chamando";
      // O teto existe para o servidor que não fechou o toque (job parado): ninguém mais vai encerrar a sessão, e
      // ela ficaria aberta (o `/estado` seguiria `em_curso`) para a próxima ligação esbarrar. Então, além de
      // fechar a tela, encerra no servidor: `/end` com o motivo "timeout" na conversa direta (o servidor grava
      // `nao_atendida`, como o cron; "normal" gravaria `cancelada`, "quem ligou desistiu", e o `end_reason` mentiria)
      // e `/leave` no grupo (que não tem motivo). Depois do `expira_em` o `/join` já não deixa ninguém atender, então
      // não há conversa a derrubar. O texto aparece sem esperar a resposta; a falha do pedido não vira erro na tela.
      const terminarPorNinguemAtender = () => {
        terminar(textoDeNinguemAtendeu(atual));
        void encerrarNoServidor(atual, "timeout").catch(() => undefined);
      };
      if (estado === null) {
        if (ninguemAtendeu) terminarPorNinguemAtender();
        return;
      }
      if (estado.situacao === "encerrada") {
        aplicarFim(atual, estado.desfecho ?? "normal");
      } else if (estado.atendida) {
        if (atual.fase === "chamando" && atual.souQuemLigou) {
          definirLigacao({
            ...atual,
            fase: "em_curso",
            callSessionId: id,
            iniciadaEm: new Date().toISOString(),
          });
        }
      } else if (ninguemAtendeu) {
        terminarPorNinguemAtender();
      }
    },
    [aplicarFim, definirLigacao, encerrarNoServidor, terminar]
  );

  /* ── as ligações chegando ────────────────────────────────────────────────────────────────────── */

  const [chegando, setChegando] = useState<ChamadaChegando[]>([]);
  const chegandoRef = useRef<ChamadaChegando[]>([]);
  /** O prazo de cada aviso, contado de quando o evento chegou. */
  const prazos = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  /** O que o leitor de tela ouve quando o foco NÃO pôde ir para Atender (a pessoa estava digitando). */
  const [anuncio, setAnuncio] = useState<{ id: string; texto: string } | null>(null);

  const tirarDeChegando = useCallback((id: string) => {
    const prazo = prazos.current.get(id);
    if (prazo) clearTimeout(prazo);
    prazos.current.delete(id);
    if (!chegandoRef.current.some((c) => c.call_session_id === id)) return;
    chegandoRef.current = chegandoRef.current.filter((c) => c.call_session_id !== id);
    setChegando(chegandoRef.current);
  }, []);

  const entrarEmChegando = useCallback(
    (nova: ChamadaChegando) => {
      const id = nova.call_session_id;
      // Sem repetir id: a repetição não reinicia o prazo (ele conta da 1ª chegada).
      if (chegandoRef.current.some((c) => c.call_session_id === id)) return;
      chegandoRef.current = [...chegandoRef.current, nova];
      setChegando(chegandoRef.current);
      prazos.current.set(
        id,
        setTimeout(() => tirarDeChegando(id), prazoDoToqueEmMs(nova.segundos_restantes))
      );
      // Com a pessoa digitando o foco não vai para Atender (o `AvisoDeChamada` decide pela mesma regra); aí
      // quem avisa o leitor de tela é a região `role="alert"` da pilha.
      if (focoEmCampoEditavel()) {
        setAnuncio({
          id,
          texto: `${nova.quem_ligou.nome} está ligando · ${nova.midia === "video" ? "vídeo" : "voz"}`,
        });
      }
    },
    [tirarDeChegando]
  );

  const isInCall = ligacao !== null && ligacao.fase !== "terminou";
  /**
   * Quantos `atender` estão trocando uma ligação em curso por outra (A1). O `atender` fecha a janela velha
   * (`isInCall` vira falso) ANTES do `/end` e do `join`, e o aviso só sai no fim: sem esta marca a campainha
   * alta voltaria por 1–2 s no meio da troca. Só conta quando havia ligação para trocar; quem atende sem
   * estar em ligação segue com a campainha até o aviso sair, como sempre.
   */
  const [trocasDeLigacao, setTrocasDeLigacao] = useState(0);

  // O som toca enquanto houver QUALQUER aviso; um só, por aparelho (a trava é do `toque.ts`). Quem já está
  // numa ligação (ou está largando uma para atender outra) ouve o bipe baixo de chamada em espera; quem não
  // está, a campainha alta. O "Som neste aparelho" não é consultado, como na campainha. Dois avisos e um
  // atendido: o `stop` da campainha roda e o bipe começa; sem aviso nenhum, silêncio.
  const temChegando = chegando.length > 0;
  const tocaEmEspera = isInCall || trocasDeLigacao > 0;
  useEffect(() => {
    if (!temChegando) return undefined;
    return tocaEmEspera ? tocarBipeDeEspera() : tocarCampainha();
  }, [temChegando, tocaEmEspera]);

  // A decisão vale por APARELHO, não por aba (I1): quem está numa ligação segura a trava compartilhada
  // "seweb:em-ligacao", e a campainha de QUALQUER outra aba, ao ganhar a trava do som, toca o bipe no lugar da
  // campainha alta (`toque.ts`). Sem isto, com a ligação na aba A e a B em outro módulo, a B podia ganhar a trava e
  // tocar a campainha alta no meio da conversa. Segue a mesma condição do bipe desta aba (`tocaEmEspera`), então a
  // troca de uma ligação por outra não solta a trava no meio. Sai quando a ligação acaba e quando o provider desmonta.
  useEffect(() => {
    if (!tocaEmEspera) return undefined;
    return marcarEmLigacao();
  }, [tocaEmEspera]);

  // Quem liga ouve o "chamando" enquanto o outro lado toca. No grupo grande ninguém toca ("Aguardando alguém
  // entrar…"): sem som, ou o "tuuu" correria minutos até alguém entrar pela faixa.
  const chamandoComoQuemLigou =
    ligacao?.fase === "chamando" && ligacao.souQuemLigou && !aguardandoAlguemEntrar(ligacao);
  useEffect(() => {
    if (!chamandoComoQuemLigou) return undefined;
    return tocarChamando();
  }, [chamandoComoQuemLigou]);

  // O teto da discagem (R1): 35 s depois de a discagem ter id, quem liga ainda em `chamando` pergunta ao servidor.
  // Dependências primitivas: o teto conta de quando a discagem começou e não reinicia com cada mudança do objeto
  // da ligação. Sai junto com a fase (atendeu, terminou, cancelou). O grupo grande (`tocaPara` 0) não tem teto:
  // ninguém toca, e a ligação espera alguém entrar pela faixa por quanto tempo for.
  const faseDaLigacao = ligacao?.fase ?? null;
  const euLigueiNaLigacao = ligacao?.souQuemLigou ?? false;
  const idDaLigacao = ligacao?.callSessionId ?? null;
  const esperandoGente = ligacao !== null && aguardandoAlguemEntrar(ligacao);
  useEffect(() => {
    if (faseDaLigacao !== "chamando" || !euLigueiNaLigacao || idDaLigacao === null)
      return undefined;
    if (esperandoGente) return undefined;
    const teto = setTimeout(() => {
      void conferirPeloServidor("teto");
    }, TETO_DA_DISCAGEM_S * 1000);
    return () => clearTimeout(teto);
  }, [faseDaLigacao, euLigueiNaLigacao, idDaLigacao, esperandoGente, conferirPeloServidor]);

  // O sino voltou depois de uma queda: o `atendida`/`encerrada` que passou nela não volta (R2).
  useEffect(
    () => ouvirVoltaDoSino(() => void conferirPeloServidor("volta-do-sino")),
    [conferirPeloServidor]
  );

  /* ── os eventos do servidor ──────────────────────────────────────────────────────────────────── */

  const aoReceberEvento = useCallback(
    (evento: EventoDeChamada) => {
      switch (evento.type) {
        case "chamada:chegando":
          entrarEmChegando(evento);
          break;
        case "chamada:parou":
          tirarDeChegando(evento.call_session_id);
          break;
        case "chamada:atendida": {
          const atual = ligacaoRef.current;
          // Com o id ainda nulo (o `/initiate` não respondeu) o `atendida` na frente da resposta vale: só existe
          // uma discagem por vez, e perdê-lo deixaria a ligação em `chamando` com o "tuuu" tocando por cima da
          // conversa (voz) ou a janela de vídeo sem abrir. O `startCall` confere o id quando o `/initiate` voltar.
          if (
            atual?.fase === "chamando" &&
            atual.souQuemLigou &&
            (atual.callSessionId === null || atual.callSessionId === evento.call_session_id)
          ) {
            definirLigacao({
              ...atual,
              fase: "em_curso",
              callSessionId: evento.call_session_id,
              iniciadaEm: new Date().toISOString(),
            });
          }
          break;
        }
        case "chamada:encerrada": {
          const id = evento.call_session_id;
          tirarDeChegando(id); // quem ligou desistiu: o aviso sai
          const atual = ligacaoRef.current;
          if (atual && atual.fase !== "terminou" && atual.callSessionId === null) {
            // Discando ainda: o fim chegou na frente da resposta do `/initiate` (R3). Ainda não há id para casar,
            // então guarda; o `startCall` o aplica se a resposta trouxer este mesmo id.
            fimAntecipado.current = { id, desfecho: evento.desfecho };
            break;
          }
          if (!atual || atual.fase === "terminou" || atual.callSessionId !== id) break;
          aplicarFim(atual, evento.desfecho);
          break;
        }
        default:
          // O servidor pode crescer um `chamada:*` novo: o que o front não conhece, ignora.
          break;
      }
    },
    [aplicarFim, definirLigacao, entrarEmChegando, tirarDeChegando]
  );

  useEffect(() => ouvirEventosDeChamada(aoReceberEvento), [aoReceberEvento]);

  /* ── as ações ────────────────────────────────────────────────────────────────────────────────── */

  const startCall = useCallback(
    async (args: StartCallArgs) => {
      const atual = ligacaoRef.current;
      // Já há uma ligação ativa: os botões de ligar ficam desligados enquanto ela dura.
      if (atual && atual.fase !== "terminou") return;

      // Um fim guardado é de uma discagem anterior: não vale para esta.
      fimAntecipado.current = null;
      abrirLigacao({
        fase: "chamando",
        callSessionId: null,
        roomId: args.room_id ?? null,
        roomType: args.room_type ?? (args.target_user_id ? "DM" : null),
        outroLadoId: args.target_user_id ?? args.outro_lado_id ?? null,
        midia: args.midia,
        titulo: args.titulo,
        souQuemLigou: true,
        tocaPara: 0,
        iniciadaEm: null,
        aviso: null,
      });

      iniciando.current = true;
      try {
        const resposta = await callRef.current.initiate({
          targetUserId: args.target_user_id ?? null,
          targetRoomId: args.room_id ?? null,
          mediaTypes: args.midia === "video" ? ["video", "audio"] : ["audio"],
        });
        iniciando.current = false;
        const antecipado = fimAntecipado.current;
        fimAntecipado.current = null;
        if (cancelarAoResponder.current) {
          // Cancelei no meio do caminho: agora há id, então encerra de verdade (o servidor grava `cancelada`).
          // No grupo só SAI: alguém pode ter atendido no intervalo (o `atendida` chega antes da resposta) e o
          // `/end` de quem iniciou derrubaria a ligação dele. Não dá para usar `encerrarNoServidor`, que sai cedo
          // com `callSessionId === null` (a ligação ainda pode estar sem id aqui).
          cancelarAoResponder.current = false;
          const aoCancelar = ligacaoRef.current;
          if (aoCancelar && ehGrupo(aoCancelar)) await callRef.current.leave();
          else await callRef.current.end("normal");
          definirLigacao(null);
          return;
        }
        // O id do `/initiate` é o que vale. Se o `atendida` aceito sem id era de outra sessão, a ligação
        // volta a `chamando` com o id certo; se era desta, continua `em_curso`.
        definirLigacao((a) => {
          if (!a || !a.souQuemLigou || a.fase === "terminou") return a;
          const sessao = { roomId: resposta.roomId ?? a.roomId, tocaPara: resposta.tocaPara };
          if (a.fase === "em_curso" && a.callSessionId !== resposta.callSessionId) {
            return {
              ...a,
              ...sessao,
              fase: "chamando",
              callSessionId: resposta.callSessionId,
              iniciadaEm: null,
            };
          }
          return { ...a, ...sessao, callSessionId: resposta.callSessionId };
        });
        // O fim chegou na frente desta resposta e era DESTA sessão (R3): agora há id, aplica.
        if (antecipado && antecipado.id === resposta.callSessionId) {
          const agora = ligacaoRef.current;
          if (agora && agora.fase !== "terminou") aplicarFim(agora, antecipado.desfecho);
        }
      } catch (erro) {
        iniciando.current = false;
        if (cancelarAoResponder.current) {
          // Cancelei e o `/initiate` falhou: não há ligação nenhuma, fecha em silêncio.
          cancelarAoResponder.current = false;
          definirLigacao(null);
          return;
        }
        terminar(textoDoErroAoLigar(erro));
      }
    },
    [abrirLigacao, aplicarFim, definirLigacao, terminar]
  );

  const atendendo = useRef(new Set<string>());

  const atender = useCallback(
    async (id: string, tituloDaFaixa?: string) => {
      const atual = ligacaoRef.current;
      // Já estou nesta ligação, ou o toque já está sendo atendido (duplo toque).
      if (atual && atual.fase !== "terminou" && atual.callSessionId === id) return;
      if (atendendo.current.has(id)) return;
      atendendo.current.add(id);
      let trocouDeLigacao = false;
      try {
        // 1) De onde vêm a mídia, o título e a sala: do aviso; sem ele (a faixa "Entrar" do grupo), do servidor.
        const item = chegandoRef.current.find((c) => c.call_session_id === id);
        let meta: Pick<LigacaoComum, "roomId" | "roomType" | "outroLadoId" | "midia" | "titulo">;
        if (item) {
          const tipo = tipoDaSala(item.room_type);
          meta = {
            roomId: item.room_id,
            roomType: tipo,
            outroLadoId: tipo === "DM" ? item.quem_ligou.id : null,
            midia: item.midia,
            titulo: tituloDaFaixa || item.sala || item.quem_ligou.nome,
          };
        } else {
          let estado: EstadoDaLigacao;
          try {
            estado = await estadoDaLigacao(id);
          } catch (erro) {
            if (statusDoErro(erro) === 404) {
              toast(TEXTO_JA_TERMINOU);
              return;
            }
            throw erro;
          }
          if (estado.situacao === "encerrada") {
            toast(TEXTO_JA_TERMINOU);
            return;
          }
          const tipo = tipoDaSala(estado.room_type);
          meta = {
            roomId: estado.room_id,
            roomType: tipo,
            outroLadoId: tipo === "DM" ? estado.quem_ligou.id : null,
            midia: estado.midia,
            titulo: tituloDaFaixa || estado.sala || estado.quem_ligou.nome,
          };
        }

        // 2) Atender no meio de outra ligação encerra a atual ANTES de entrar na nova: o hook rastreia UMA sessão.
        const emCurso = ligacaoRef.current;
        if (emCurso && emCurso.fase !== "terminou") {
          if (emCurso.callSessionId === null || iniciando.current) {
            // Ainda discando (o `/initiate` não respondeu): sem sessão no hook não há como encerrar, e entrar agora
            // misturaria as duas fichas no hook. Quem chamou mostra o erro; um instante depois funciona.
            throw new Error("A ligação atual ainda está sendo criada.");
          }
          // Fecha a janela velha ANTES de esperar o servidor: o desligar da sala dela (que cai quando o servidor a
          // apaga) não acha mais ligação para encerrar, e o `/end` não sai duas vezes.
          definirLigacao(null);
          // No mesmo lote do `definirLigacao(null)`: o som do aviso não vê a janela fechada sem a troca marcada.
          trocouDeLigacao = true;
          setTrocasDeLigacao((n) => n + 1);
          await encerrarNoServidor(emCurso);
        }

        // 3) Entra. O 409 ("A ligação já terminou.") RESOLVE: o cartão sai e o toast é visível. As outras
        // falhas rejeitam SEM toast: no celular ele ficaria atrás do aviso; o cartão mostra o erro em linha.
        try {
          await callRef.current.join(id);
        } catch (erro) {
          if (statusDoErro(erro) === 409) {
            tirarDeChegando(id);
            toast(TEXTO_JA_TERMINOU);
            return;
          }
          throw erro;
        }
        tirarDeChegando(id);
        abrirLigacao({
          ...meta,
          fase: "em_curso",
          callSessionId: id,
          souQuemLigou: false,
          tocaPara: 0,
          iniciadaEm: new Date().toISOString(),
          aviso: null,
        });
      } finally {
        atendendo.current.delete(id);
        // Junto com o `abrirLigacao` (ou com a falha): a ligação nova já conta como "em outra ligação".
        if (trocouDeLigacao) setTrocasDeLigacao((n) => n - 1);
      }
    },
    [abrirLigacao, definirLigacao, encerrarNoServidor, tirarDeChegando]
  );

  const recusar = useCallback(
    async (id: string) => {
      try {
        await recusarLigacao(id);
      } catch (erro) {
        // 409: a ligação já não toca para a pessoa (quem ligou cancelou, os 30 s venceram, atendeu em outro
        // aparelho). O efeito pedido aconteceu, então não há erro a mostrar: o aviso só sai.
        // Parar de tocar para esta pessoa vale mais que a confirmação do servidor: o aviso sai de qualquer jeito.
        if (statusDoErro(erro) !== 409) toast.error("Não foi possível recusar.");
      }
      tirarDeChegando(id);
    },
    [tirarDeChegando]
  );

  /**
   * Desligar (o botão vermelho, a sala que caiu, `endCall`). IDEMPOTENTE: depois de desligar a ligação some, e
   * em `terminou` não há mais o que encerrar — o desmonte da sala (que dispara `onDisconnected`) não repete o
   * `/end`. Antes de o `/initiate` responder não há id: marca "cancelado" e o `startCall` encerra quando houver.
   */
  const desligar = useCallback(() => {
    const atual = ligacaoRef.current;
    if (!atual || atual.fase === "terminou") return;
    if (atual.callSessionId === null || iniciando.current) {
      // O hook ainda não conhece a sessão (o `/initiate` não respondeu, mesmo que o `atendida` já tenha dado
      // o id à ligação): `end`/`leave` não fariam nada. O `startCall` encerra quando houver sessão.
      cancelarAoResponder.current = true;
      return;
    }
    void encerrarNoServidor(atual).catch(() => undefined);
    definirLigacao(null);
  }, [definirLigacao, encerrarNoServidor]);

  /**
   * O desligar que a JANELA chama (o botão, a sala que caiu, o `onEnd` do vídeo): ligado ao id da ligação que
   * ela desenhou. Uma janela velha cuja ligação já acabou (a sala dela caiu tarde, o `onEnd` do vídeo
   * disparou no desmonte) não pode desligar a ligação SEGUINTE. Sem id (antes de o `/initiate` responder) vale
   * a atual: só há uma, e o clique é da pessoa.
   */
  const desligarSeForA = useCallback(
    (id: string | null) => {
      if (id !== null && ligacaoRef.current?.callSessionId !== id) return;
      desligar();
    },
    [desligar]
  );

  /* ── o aviso que vem do push: o link ?ligacao=<id> e a mensagem do service worker ─────────────── */

  /**
   * O fluxo COMUM dos dois caminhos do push: pergunta ao servidor pela ligação e, se ainda toca para esta pessoa, entra
   * em `chegando` (o aviso aparece e toca). O toast "A ligação já terminou." é só para a ligação que TERMINOU
   * (`encerrada`, 404 ou falha de rede): a que segue em curso, ou que toca para outra pessoa/aparelho, não diz isso —
   * fica em silêncio, e a faixa "Entrar" da conversa convida. Devolve `true` quando o aviso ficou de pé. Nunca lança.
   */
  const abrirAvisoDaLigacao = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const estado = await estadoDaLigacao(id);
        if (estado.situacao === "tocando" && estado.tocando_para_mim) {
          entrarEmChegando({
            type: "chamada:chegando",
            call_session_id: estado.call_session_id,
            room_id: estado.room_id ?? "",
            room_type: estado.room_type ?? "",
            // O rótulo vem pronto do servidor: o nome do grupo, ou o de quem ligou na conversa direta.
            sala: estado.sala,
            quem_ligou: estado.quem_ligou,
            midia: estado.midia,
            expira_em: estado.expira_em ?? "",
            segundos_restantes: estado.segundos_restantes ?? 0,
          });
          return true;
        }
        if (estado.situacao === "encerrada") toast(TEXTO_JA_TERMINOU);
      } catch {
        // 404 (a pessoa não vê a ligação, ou ela não existe) ou falha de rede: o link velho avisa que a ligação terminou.
        toast(TEXTO_JA_TERMINOU);
      }
      return false;
    },
    [entrarEmChegando]
  );

  const idDaUrl = new URLSearchParams(local.search).get("ligacao");
  const lendoDaUrl = useRef<string | null>(null);

  useEffect(() => {
    if (!idDaUrl || lendoDaUrl.current === idDaUrl) return;
    lendoDaUrl.current = idDaUrl;
    // O parâmetro sai da URL (o resto fica): recarregar a página não reabre a ligação.
    const parametros = new URLSearchParams(local.search);
    parametros.delete("ligacao");
    const busca = parametros.toString();
    navigate(
      { pathname: local.pathname, search: busca ? `?${busca}` : "", hash: local.hash },
      { replace: true }
    );

    void abrirAvisoDaLigacao(idDaUrl).finally(() => {
      lendoDaUrl.current = null;
    });
  }, [idDaUrl, local.pathname, local.search, local.hash, navigate, abrirAvisoDaLigacao]);

  /**
   * A pessoa tocou no corpo do aviso do celular/desktop, ou em "Atender": o service worker achou esta janela aberta,
   * focou-a e mandou a ligação até aqui (em vez de abrir uma segunda instância do app). Roda o mesmo fluxo do
   * `?ligacao=` SEM mexer na URL; com `atender`, e a ligação ainda tocando para a pessoa, atende direto. Um id que já
   * está em `chegando` (o evento do WebSocket chegou antes) não duplica o aviso: só atende, se for o caso.
   */
  const abrirPeloAviso = useCallback(
    async (id: string, atenderLogo: boolean) => {
      const jaToca = chegandoRef.current.some((c) => c.call_session_id === id);
      const tocando = jaToca || (await abrirAvisoDaLigacao(id));
      if (!atenderLogo || !tocando) return;
      try {
        await atender(id);
      } catch {
        // `atender` rejeita sem toast (o cartão mostra o erro em linha); aqui a ordem veio do push e o cartão não
        // clicou: o toast é o único lugar. O cartão segue na tela, e a pessoa pode tentar de novo nele.
        toast.error(TEXTO_NAO_ATENDEU);
      }
    },
    [abrirAvisoDaLigacao, atender]
  );

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return undefined;
    const servico = navigator.serviceWorker;
    const aoReceberMensagem = (evento: MessageEvent) => {
      if (ehPerguntaDaChamada(evento.data)) {
        // O service worker pergunta, antes de abafar o push da ligação, se ESTA página vai tocá-la. Tocar depende do sino
        // de pé (WebSocket ou SSE); `aberta` é do clique no aviso (o provider está montado). Quem não tem `CallProvider`
        // (login, portal, manutenção) não responde, e o silêncio vale como "não".
        const resposta: RespostaDaPerguntaDaChamada = {
          tocar: ["ws", "sse"].includes(getTransportMode()),
          aberta: true,
        };
        evento.ports[0]?.postMessage(resposta);
        return;
      }
      if (ehMensagemDeIr(evento.data)) {
        // O clique no push final ("Chamada perdida"...) com esta página aberta: o service worker não a navega
        // (`WindowClient.navigate` recarrega e derrubaria a ligação em curso), pede que ela ande pelo router. Com `?ligacao=`
        // na url cai no efeito do link (`idDaUrl`), o mesmo fluxo. `ehMensagemDeIr` já descartou a url que não é caminho do app.
        navigateRef.current(evento.data.url);
        return;
      }
      if (!ehMensagemDaChamada(evento.data)) return; // o resto é de outro ouvinte
      void abrirPeloAviso(evento.data.call_session_id, evento.data.atender);
    };
    servico.addEventListener("message", aoReceberMensagem);
    return () => servico.removeEventListener("message", aoReceberMensagem);
  }, [abrirPeloAviso]);

  /* ── sair do sistema ─────────────────────────────────────────────────────────────────────────── */

  // O `logout()` apaga o token de forma síncrona e o provider só desmonta depois: o `/end` do desmonte sairia sem
  // `Authorization`. O gancho recebe o token de quem sai ANTES da limpeza e encerra a ligação ativa que já tem id.
  // Discando ainda (sem id) não há o que encerrar aqui: o `startCall` só encerra quando o `/initiate` responder, e
  // esse pedido já sai sem token (o logout o apagou) e leva 401. A ligação acaba pelo job dos 30 s ou pelo cron do
  // servidor, não pelo "Sair". Encerrar com o token capturado quando o `/initiate` responder é item do Roteiro.
  useEffect(
    () =>
      registrarAntesDeSair((tokenDeQuemSai, opcoes) => {
        const atual = ligacaoRef.current;
        if (!atual || atual.fase === "terminou" || atual.callSessionId === null) return;
        // "Sair de todos os aparelhos": o servidor já revogou o access, então o pedido levaria 401 (ruído no console). A ligação
        // acaba pelo job dos 30 s ou pelo cron do servidor, como no "discando ainda" acima.
        if (!opcoes?.semRede) {
          encerrarLigacaoAoSair(tokenDeQuemSai, { id: atual.callSessionId, grupo: ehGrupo(atual) });
        }
        // A marca é local e vale nos dois casos: sem ela o desmonte mandaria o `/end` sem token (401).
        encerradaAoSair.current = atual.callSessionId;
      }),
    []
  );

  /* ── desmontar ───────────────────────────────────────────────────────────────────────────────── */

  useEffect(
    () => () => {
      // O provider sai (logout, fim da sessão) com ligação ativa: encerra no servidor, senão ela fica pendurada.
      const atual = ligacaoRef.current;
      if (atual && atual.fase !== "terminou") {
        // O "Sair" já a encerrou com o token de quem saiu: um 2º pedido agora sairia sem token (401).
        const jaEncerradaAoSair =
          atual.callSessionId !== null && encerradaAoSair.current === atual.callSessionId;
        // Discando ainda: o `startCall` tenta encerrar quando o `/initiate` responder (o hook sobrevive ao provider);
        // depois do "Sair" esse pedido sai sem token (ver o gancho acima).
        if (iniciando.current) cancelarAoResponder.current = true;
        else if (!jaEncerradaAoSair) void encerrarNoServidor(atual).catch(() => undefined);
      }
      for (const prazo of prazos.current.values()) clearTimeout(prazo);
      prazos.current.clear();
      if (temporizadorDeFechar.current) clearTimeout(temporizadorDeFechar.current);
      if (esperaDaLeitura.current) clearTimeout(esperaDaLeitura.current);
    },
    [encerrarNoServidor]
  );

  /* ── o que o contexto entrega e o que o provider desenha ─────────────────────────────────────── */

  const value = useMemo<CallContextValue>(
    () => ({
      startCall,
      atender,
      recusar,
      endCall: desligar,
      ligacao,
      chegando,
      isInCall,
      status: call.status,
    }),
    [startCall, atender, recusar, desligar, ligacao, chegando, isInCall, call.status]
  );

  // A ficha da sala é a do hook, mas só vale se é da ligação de AGORA: o `atendida` que chega antes da resposta
  // do `/initiate` dá o id à ligação ANTES de o hook gravar o token novo, e depois de um `/end` que falhou o
  // hook ainda guarda o token da anterior — que não pode abrir sala na seguinte.
  const sala =
    ligacao !== null &&
    call.token &&
    call.wsUrl &&
    call.callSessionId !== null &&
    call.callSessionId === ligacao.callSessionId
      ? { token: call.token, wsUrl: call.wsUrl }
      : null;

  const anuncioNaTela =
    anuncio && chegando.some((c) => c.call_session_id === anuncio.id) ? anuncio.texto : null;

  return (
    <CallContext.Provider value={value}>
      {children}
      {/* Acima de tudo (z-[110]): ligação chegando vale mais que o sino (100) e o sonner (99). */}
      {chegando.length > 0 &&
        createPortal(
          <div
            data-testid="pilha-de-avisos-de-chamada"
            className="pointer-events-none fixed inset-x-3 top-[calc(env(safe-area-inset-top,0px)+4.25rem)] z-[110] flex flex-col gap-2 md:inset-x-auto md:right-4 md:top-20 md:w-[22rem]"
          >
            {anuncioNaTela && (
              <div role="alert" className="sr-only" data-testid="anuncio-de-chamada">
                {anuncioNaTela}
              </div>
            )}
            {chegando.map((c) => (
              <AvisoDeChamada
                key={c.call_session_id}
                chamada={c}
                emOutraLigacao={isInCall}
                onAtender={() => atender(c.call_session_id)}
                onRecusar={() => recusar(c.call_session_id)}
              />
            ))}
          </div>,
          document.body
        )}
      {ligacao && (
        <JanelaDaLigacao
          key={geracao}
          ligacao={ligacao}
          sala={sala}
          onDesligar={() => desligarSeForA(ligacao.callSessionId)}
          onFechar={fechar}
          onAlguemEntrou={() => void conferirPeloServidor("entrou-gente")}
        />
      )}
    </CallContext.Provider>
  );
}

/** Sem o provider (testes soltos, árvore sem `CallProvider`): valor inerte, sem `alert`. */
const SEM_PROVIDER: CallContextValue = {
  startCall: async () => {
    console.warn("[CallContext] CallProvider não montado: a ligação não foi iniciada.");
  },
  atender: async () => {},
  recusar: async () => {},
  endCall: () => {},
  ligacao: null,
  chegando: [],
  isInCall: false,
  status: "idle",
};

export function useCallContext(): CallContextValue {
  return useContext(CallContext) ?? SEM_PROVIDER;
}
